import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useAuthState } from '@/hooks/useAuthState';
import { hasActiveAccess } from '@/services/organization.service';
import { Footer } from '@/components/Footer';
import './styles/LandingPage.css';

export function LandingPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuthState();

  useEffect(() => {
    async function checkAuthAndRedirect() {
      if (loading) return;

      if (user) {
        // User is signed in, check subscription
        const hasAccess = await hasActiveAccess(user.uid);

        if (hasAccess) {
          navigate('/booth');
        } else {
          navigate('/billing');
        }
      }
    }

    checkAuthAndRedirect();
  }, [user, loading, navigate]);

  // Scroll animation for revealed sections
  useEffect(() => {
    const observerOptions = {
      threshold: 0.2,
      rootMargin: '0px 0px -100px 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, observerOptions);

    const revealed = document.querySelectorAll('.reveal');
    revealed.forEach((el) => observer.observe(el));

    return () => {
      revealed.forEach((el) => observer.unobserve(el));
    };
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="landing-page">
      {/* Navbar */}
      <nav className="navbar">
        <div className="nav-container">
          <img
            src="/assets/images/logo-long-blue.png"
            alt="Sunday Best"
            className="logo"
          />
          <div className="nav-links">
            <button
              onClick={() => scrollToSection('home')}
              className="nav-link"
            >
              Home
            </button>
            <button
              onClick={() => scrollToSection('how-it-works')}
              className="nav-link"
            >
              How It Works
            </button>
            <button
              onClick={() => scrollToSection('features')}
              className="nav-link"
            >
              Features
            </button>
            <button
              onClick={() => scrollToSection('pricing')}
              className="nav-link"
            >
              Pricing
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="nav-link"
            >
              Contact
            </button>
            <button
              onClick={() => navigate('/login')}
              className="nav-link-login"
            >
              Login
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              Turning Memorable Moments Into Connections
            </h1>
            <p className="hero-hook">
              It&rsquo;s hard to connect with new guests.
            </p>
            <p className="hero-subtitle">
              Sunday Best is a photo booth app that creates a simple and
              natural opportunity to make those connections, so your team can
              easily follow up and build your community.
            </p>
            <div className="hero-actions">
              <button onClick={() => navigate('/login')} className="cta-button">
                Get Started Today
              </button>
              <button
                onClick={() => scrollToSection('how-it-works')}
                className="cta-secondary"
              >
                See How It Works
              </button>
            </div>
          </div>
          <div className="hero-photo-wrap">
            <div className="photo-strip">
              <img
                src="/assets/images/booth-guests.jpg"
                alt="Two guests posing at a Sunday Best photo booth"
              />
              <img
                src="/assets/images/booth-form.jpg"
                alt="A guest filling out a short form on the photo booth"
              />
              <img
                src="/assets/images/booth-done.jpg"
                alt="Photo booth screen confirming photos are on their way"
              />
              <span className="photo-strip-caption">Sunday Best</span>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="how-section">
        <div className="section-container">
          <h2 className="section-title">How It Works</h2>

          <div className="how-steps">
            {/* Step 1 */}
            <div className="how-step reveal">
              <div className="how-photo-wrap">
                <img
                  src="/assets/images/booth-guests.jpg"
                  alt="Photo booth set out at an event with guests posing"
                  className="how-photo"
                />
              </div>
              <div className="how-text">
                <span className="step-badge">Step 01</span>
                <h3>Set out the photo booth</h3>
                <p>
                  Place the booth at your event. It fits right in at services,
                  store openings, community gatherings, holidays, and
                  everything in between.
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="how-step reversed reveal">
              <div className="how-photo-wrap">
                <img
                  src="/assets/images/booth-form.jpg"
                  alt="A guest filling out a short form on the photo booth"
                  className="how-photo"
                />
              </div>
              <div className="how-text">
                <span className="step-badge">Step 02</span>
                <h3>Guests take fun photos</h3>
                <p>
                  Guests snap their photos and complete a short, customizable
                  form. It&rsquo;s a natural moment to share their name, email,
                  or anything else you&rsquo;d like to know.
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="how-step reveal">
              <div className="how-photo-wrap">
                <img
                  src="/assets/images/booth-done.jpg"
                  alt="Photo booth screen confirming photos are on their way"
                  className="how-photo"
                />
              </div>
              <div className="how-text">
                <span className="step-badge">Step 03</span>
                <h3>Photos delivered, forms received</h3>
                <p>
                  Guests are emailed their photos, and you receive their form.
                  Follow-up stays simple, and every guest leaves with a
                  keepsake to remember the moment.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        <div className="section-container">
          <h2 className="section-title">For Your Organization</h2>

          <div className="features-grid">
            <div className="feature-card reveal">
              <div className="feature-card-shot">
                <img
                  src="/assets/images/dashboard-forms.png"
                  alt="Sunday Best questionnaire editor with customizable guest questions"
                />
              </div>
              <h3>Customizable Forms</h3>
              <p>
                Create a new form tailored to each event, whether you want to
                collect a name and email, gather information from first-time
                guests, or learn more about your community.
              </p>
            </div>
            <div className="feature-card reveal">
              <div className="feature-card-shot">
                <img
                  src="/assets/images/dashboard-data.jpg"
                  alt="Sunday Best dashboard showing guest totals, sessions, and activity"
                />
              </div>
              <h3>Gathered Event Data</h3>
              <p>
                After the event, access the form information and photos from
                every participant, making follow-up or adding new members to
                your communications easy and seamless.
              </p>
            </div>
          </div>

          <p className="features-tagline reveal">
            Capture meaningful information for your team to connect, follow
            up, and care for people well.
          </p>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing-section">
        <div className="section-container">
          <h2 className="section-title">Simple Pricing</h2>
          <p className="pricing-subtitle">
            Flat rate. Unlimited use across every event and season. Cancel
            anytime.
          </p>
          <div className="pricing-grid">
            <div className="pricing-card">
              <h3>Monthly</h3>
              <div className="price">
                <span className="currency">$</span>
                <span className="amount">29</span>
                <span className="period">/month</span>
              </div>
              <ul className="pricing-features">
                <li>✓ Unlimited photos</li>
                <li>✓ Custom forms for every event</li>
                <li>✓ Photos emailed to guests</li>
                <li>✓ Admin dashboard</li>
              </ul>
              <button
                onClick={() => navigate('/login', { state: { signup: true } })}
                className="pricing-button"
              >
                Start Free Trial
              </button>
            </div>
            <div className="pricing-card featured">
              <div className="best-value">Best Value</div>
              <h3>Yearly</h3>
              <div className="price">
                <span className="currency">$</span>
                <span className="amount">298</span>
                <span className="period">/year</span>
              </div>
              <p className="savings">Save $50/year</p>
              <ul className="pricing-features">
                <li>✓ Everything in Monthly</li>
                <li>✓ Priority support</li>
                <li>✓ Early access to features</li>
              </ul>
              <button
                onClick={() => navigate('/login', { state: { signup: true } })}
                className="pricing-button primary"
              >
                Start Free Trial
              </button>
            </div>
          </div>
          <div className="pricing-notes">
            <p>
              We want to make it easy for your organization to use Sunday
              Best, so we&rsquo;ve kept the cost as low as possible to cover
              the data and storage fees.
            </p>
            <p className="pricing-not-included">
              Not included: photo booth stand (contact us for a product we
              recommend) and an iPad or tablet.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="section-container">
          <h2 className="section-title">Let&rsquo;s Make Magic Together</h2>
          <p className="contact-subtitle">
            Questions? Ideas? We&rsquo;d love to hear from you.
          </p>
          <div className="contact-info">
            <a href="mailto:hello@sundaybestapp.com" className="contact-link">
              hello@sundaybestapp.com
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
